import { EntreeDepuis } from '../pipeline/EntreesPipeline.js';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '../../api/mutation.js';
import { Badge, Button, Dialog, Input, Price, Select, Textarea } from '@artosera/ui';
import { useLangue } from '../../composants/useLangue.js';
import { formatDate } from '@artosera/shared';
import {
  useOffre,
  useGesteOffre,
  usePlanifierRelance,
  useSupprimerOffre,
  offresApi,
  type OffrePresentee,
  type GesteOffre,
} from '../../api/offres.js';
import { useContact } from '../../api/contacts.js';
import { reservationsApi } from '../../api/reservations.js';
import { ventesApi } from '../../api/ventes.js';
import { dansLaPortee, dansLaPorteeCourante, cleMetier } from '../../api/cles.js';
import { ouvrirUrlSignee, type AppelApi } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { FeuillePiece } from '../../composants/FeuillePiece.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { Field } from '../Field.js';
import { NouvelleOffreDialog, dateOffreParDefaut } from './NouvelleOffreDialog.js';
import { FriseOffre } from './FriseOffre.js';
import { tonEtatOffre } from './OffreCarte.js';
import { LigneEcart, LigneEcheance, PhraseOffre, TexteProchaineAction } from './OffreResume.js';
import { contexteActionDeLaGalerie, prochaineAction } from './syntheseOffre.js';
import styles from './OffreDetailPage.module.css';

export function OffreDetailPage() {
  const { id } = useParams();
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['commun']);
  const offre = useOffre(galerieSlug, id ?? null);
  if (offre.isLoading) return <p role="status">{t('chargement')}</p>;
  if (!offre.data)
    return <p role="alert">{estApiError(offre.error) ? offre.error.message : t('erreurReseau')}</p>;
  return <FicheOffre key={offre.data.offre.id} offre={offre.data.offre} />;
}
function FicheOffre({ offre }: { offre: OffrePresentee }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerie, galerieSlug } = useShellContext();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const [dateRelance, setDateRelance] = useState(offre.relancePrevueLe?.slice(0, 10) ?? '');
  const planifier = usePlanifierRelance(galerieSlug, offre.id);
  const [erreurPdf, setErreurPdf] = useState<string | null>(null);
  return (
    <section className={styles.page}>
      <div className={styles.synthese}>
        <div className={styles.resume}>
          <div className={styles.etat}>
            <Badge tone={tonEtatOffre(offre)}>{t(`regie:offres.etats.${offre.etat}`)}</Badge>
            {offre.echue ? <Badge tone="reserved">{t('regie:offres.echue')}</Badge> : null}
            {offre.canal ? (
              <span className={styles.canal}>{t(`regie:offres.fiche.canaux.${offre.canal}`)}</span>
            ) : null}
          </div>
          <PhraseOffre offre={offre} langue={langue} />
          <LigneEcart offre={offre} langue={langue} />
          <LigneEcheance offre={offre} langue={langue} />
        </div>
        <GestesOffre offre={offre} />
      </div>
      <EntreeDepuis source="offre" id={offre.id} contactId={offre.contactId} />
      {erreurPdf ? <p role="alert">{erreurPdf}</p> : null}
      <div className={styles.contenu}>
        <FeuillePiece
          emetteur={galerie.nom ?? ''}
          nature={t('regie:offres.fiche.proposition')}
          destinataire={{ legende: t('regie:offres.fiche.attention'), nom: offre.contactNom }}
          numero={offre.numero}
          brouillon={offre.etat === 'brouillon'}
          etat={offre.etat}
          meta={t('regie:offres.fiche.validite', {
            date: formatDate(new Date(offre.validiteJusquau), langue),
          })}
        >
          <div className={styles.lignes}>
            {offre.lignes.map((ligne, i) => (
              <div key={i} className={styles.ligne}>
                <ObjetCartel
                  {...ligne.cartel}
                  precisions={[ligne.cartel.dimensions, ligne.cartel.edition]}
                />
                <div className={styles.prix}>
                  {ligne.prixPublicCentimes !== null &&
                  ligne.prixPublicCentimes !== ligne.prixProposeCentimes ? (
                    <del>
                      <span className={styles.sr}>{t('regie:offres.fiche.prixDemande')} </span>
                      <Price
                        cents={ligne.prixPublicCentimes}
                        currency={offre.devise}
                        lang={langue}
                      />
                    </del>
                  ) : null}
                  <Price cents={ligne.prixProposeCentimes} currency={offre.devise} lang={langue} />
                </div>
              </div>
            ))}
          </div>
          <div className={styles.total}>
            <span>{t('regie:offres.fiche.total')}</span>
            <Price cents={offre.totalCentimes} currency={offre.devise} lang={langue} />
          </div>
          {offre.message ? <p className={styles.message}>{offre.message}</p> : null}
        </FeuillePiece>
        <aside className={styles.aside}>
          <h2>{t('regie:offres.fiche.suivi')}</h2>
          <FriseOffre offre={offre} langue={langue} />
          {offre.etat === 'envoyee' && galerieALaction(galerie, 'offers:write') ? (
            <form
              className={styles.formulaire}
              onSubmit={(e) => {
                e.preventDefault();
                void planifier.mutateAsync(dateRelance || null).catch(() => {});
              }}
            >
              <Field htmlFor="offre-relance-date" label={t('regie:offres.fiche.relancePrevue')}>
                <Input
                  id="offre-relance-date"
                  type="date"
                  value={dateRelance}
                  onChange={(e) => setDateRelance(e.target.value)}
                />
              </Field>
              <Button variant="secondary" type="submit" loading={planifier.isPending}>
                {t('commun:actions.enregistrer')}
              </Button>
              {planifier.isError ? (
                <p role="alert">
                  {estApiError(planifier.error)
                    ? planifier.error.message
                    : t('commun:erreurReseau')}
                </p>
              ) : null}
            </form>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => {
              setErreurPdf(null);
              void dansLaPorteeCourante((appel) => offresApi.pdf(galerieSlug, offre.id, appel))
                .then((rep) => ouvrirUrlSignee(rep.url))
                .catch((e) => setErreurPdf(estApiError(e) ? e.message : t('commun:erreurReseau')));
            }}
          >
            {t('regie:offres.fiche.pdf')}
          </Button>
          {/* Les réservations commerciales se suivent dans l'historique de la fiche contact. */}
          {offre.reservationIds.length > 0 && galerieALaction(galerie, 'contacts:read') ? (
            <Link to={`/${galerieSlug}/contacts/${offre.contactId}`}>
              {t('regie:offres.fiche.reservation', { count: offre.reservationIds.length })}
            </Link>
          ) : null}
          {offre.factureId &&
          galerieAModule(galerie, 'facturation') &&
          galerieALaction(galerie, 'invoices:read') ? (
            <Link to={`/${galerieSlug}/ventes/${offre.factureId}`}>
              {t('regie:offres.fiche.facture')}
            </Link>
          ) : null}
        </aside>
      </div>
    </section>
  );
}
type DialogueOffre = GesteOffre | 'modifier' | 'supprimer' | 'contrePropose';

export function GestesOffre({ offre }: { offre: OffrePresentee }) {
  const { galerie, galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const navigate = useNavigate();
  const client = useQueryClient();
  const contact = useContact(galerieSlug, offre.contactId);
  const geste = useGesteOffre(galerieSlug, offre.id);
  const supprimer = useSupprimerOffre(galerieSlug, offre.id);
  const langue = useLangue();
  const [params, setParams] = useSearchParams();
  const [dialogue, setDialogueBrut] = useState<DialogueOffre | null>(() => {
    // Les cartes de la liste ouvrent la fiche directement sur l'envoi ou la relance.
    const demande = params.get('action');
    if (demande === 'envoyer' && offre.etat === 'brouillon') return 'envoyer';
    if (demande === 'relancer' && offre.etat === 'envoyee') return 'relancer';
    return null;
  });
  const setDialogue = (valeur: DialogueOffre | null) => {
    setDialogueBrut(valeur);
    if (valeur === null && params.has('action')) {
      const suite = new URLSearchParams(params);
      suite.delete('action');
      setParams(suite, { replace: true });
    }
  };
  const [adresse, setAdresse] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [motif, setMotif] = useState('');
  const [resultat, setResultat] = useState<string | null>(null);
  // Une création réussie mais non liée reste réutilisable au prochain essai.
  const reservationsEnAttente = useRef(new Map<number, string>());
  const factureEnAttente = useRef<string | null>(null);
  const executerSuite = useCallback(
    async (type: 'reservation' | 'facture', appel: AppelApi) => {
      if (type === 'facture') {
        if (!factureEnAttente.current) {
          const rep = await ventesApi.creer(
            galerieSlug,
            {
              contactId: offre.contactId,
              devise: offre.devise,
              langue: offre.langue,
              lignes: offre.lignes.map((l) => ({
                oeuvreId: l.oeuvreId,
                exemplaireId: l.exemplaireId ?? undefined,
                prixUnitaireCentimes: l.prixProposeCentimes,
              })),
            },
            appel,
          );
          factureEnAttente.current = rep.facture.id;
        }
        await offresApi.lier(galerieSlug, offre.id, { factureId: factureEnAttente.current }, appel);
        return factureEnAttente.current;
      }
      const existantes = offre.reservationIds.length
        ? (
            await reservationsApi.lister(
              galerieSlug,
              { contactId: offre.contactId, etat: ['active'], parPage: 200 },
              appel,
            )
          ).reservations.filter((r) => offre.reservationIds.includes(r.id))
        : [];
      for (const [index, ligne] of offre.lignes.entries()) {
        if (
          existantes.some(
            (r) => r.oeuvreId === ligne.oeuvreId && r.exemplaireId === ligne.exemplaireId,
          )
        )
          continue;
        let id = reservationsEnAttente.current.get(index);
        if (!id) {
          const rep = await reservationsApi.creer(
            galerieSlug,
            {
              oeuvreId: ligne.oeuvreId,
              exemplaireId: ligne.exemplaireId,
              contactId: offre.contactId,
              jusquau: new Date(`${dateOffreParDefaut()}T12:00:00Z`).toISOString(),
              note: offre.numero,
            },
            appel,
          );
          id = rep.reservation.id;
          reservationsEnAttente.current.set(index, id);
        }
        await offresApi.lier(galerieSlug, offre.id, { reservationId: id }, appel);
      }
      return null;
    },
    [galerieSlug, offre],
  );
  // dansLaPortee stocke la fonction ; les refs ne sont lues qu’au clic, dans mutationFn.
  const suite = useMutation(
    // eslint-disable-next-line react-hooks/refs
    dansLaPortee({
      mutationFn: executerSuite,
      onSuccess: (id) => {
        for (const domaine of ['offres', 'reservations', 'ventes', 'oeuvres'])
          void client.invalidateQueries({ queryKey: cleMetier(galerieSlug, domaine) });
        if (id) navigate(`/${galerieSlug}/ventes/${id}`);
      },
    }),
  );
  const peutEcrire = galerieALaction(galerie, 'offers:write');
  const peutEnvoyer = galerieALaction(galerie, 'offers:send');
  const executer = async (nom: GesteOffre) => {
    setResultat(null);
    try {
      const rep = await geste.mutateAsync({
        geste: nom,
        corps: {
          ...(nom === 'envoyer' || nom === 'relancer'
            ? { destinataire: adresse ?? contact.data?.contact.emails[0], message }
            : {}),
          ...(nom === 'refuser' || nom === 'retirer' ? { motif } : {}),
        },
      });
      setResultat(rep.envoi?.resultat ?? null);
      setDialogue(null);
    } catch {
      /* L'erreur de mutation reste visible. */
    }
  };
  const occupe = geste.isPending || suite.isPending;
  const bouton = (nom: DialogueOffre, principal = false) => (
    <Button
      key={nom}
      variant={principal ? 'primary' : 'secondary'}
      disabled={occupe}
      onClick={() => (nom === 'remettre' ? void executer(nom) : setDialogue(nom))}
    >
      {t(`regie:offres.fiche.${nom}`)}
    </Button>
  );
  const peutReserver = galerieALaction(galerie, 'reservations:write') && peutEcrire;
  const peutFacturer =
    peutEcrire &&
    galerieAModule(galerie, 'facturation') &&
    galerieALaction(galerie, 'invoices:write');
  const boutonReserver = (principal: boolean) => (
    <Button
      key="reserver"
      variant={principal ? 'primary' : 'secondary'}
      loading={suite.isPending}
      disabled={offre.reservationIds.length >= offre.lignes.length}
      onClick={() => suite.mutate('reservation')}
    >
      {t('regie:offres.fiche.reserver')}
    </Button>
  );
  const boutonFacture = (principal: boolean) => (
    <Button
      key="facture"
      variant={principal ? 'primary' : 'secondary'}
      loading={suite.isPending}
      disabled={Boolean(offre.factureId)}
      onClick={() => suite.mutate('facture')}
    >
      {t('regie:offres.fiche.preparerFacture')}
    </Button>
  );
  const action = prochaineAction(offre, contexteActionDeLaGalerie(galerie));
  // Le geste qui fait avancer l'offre est en tête et en évidence ; les autres suivent, plus discrets.
  let principal: ReactNode[] = [];
  let autres: ReactNode[] = [];
  if (offre.etat === 'brouillon') {
    principal = peutEnvoyer
      ? [bouton('envoyer', true)]
      : peutEcrire
        ? [bouton('modifier', true)]
        : [];
    autres = peutEcrire
      ? [...(peutEnvoyer ? [bouton('modifier')] : []), bouton('remettre'), bouton('supprimer')]
      : [];
  } else if (offre.etat === 'envoyee') {
    const relanceDue = action === 'relancer' || action === 'echue';
    const relance = peutEnvoyer ? [bouton('relancer', relanceDue)] : [];
    const reponse = peutEcrire
      ? [
          bouton('accepter', !relanceDue),
          bouton('refuser'),
          bouton('contrePropose'),
          bouton('retirer'),
        ]
      : [];
    const tous = relanceDue
      ? [...relance, ...reponse]
      : [...reponse.slice(0, 1), ...relance, ...reponse.slice(1)];
    principal = tous.slice(0, 1);
    autres = tous.slice(1);
  } else if (offre.etat === 'acceptee') {
    const suites = [
      ...(peutFacturer ? [boutonFacture(true)] : []),
      ...(peutReserver ? [boutonReserver(!peutFacturer)] : []),
    ];
    principal = suites.slice(0, 1);
    autres = suites.slice(1).map(() => boutonReserver(false));
  }
  const courriel = dialogue === 'envoyer' || dialogue === 'relancer';
  return (
    <>
      <div className={styles.etape}>
        <h2 className={styles.etapeTitre}>{t('regie:offres.fiche.prochaineEtape')}</h2>
        <TexteProchaineAction offre={offre} action={action} langue={langue} />
        {action !== 'rien' ? (
          <p className={styles.aide}>
            {t(`regie:offres.fiche.aide.${action}`, { contact: offre.contactNom })}
          </p>
        ) : null}
        {principal.length ? <div className={styles.gestes}>{principal}</div> : null}
        {autres.length ? (
          <div className={styles.autres}>
            <span>{t('regie:offres.fiche.autresActions')}</span>
            <div className={styles.gestes}>{autres}</div>
          </div>
        ) : null}
      </div>
      {geste.isError || supprimer.isError || suite.isError ? (
        <p role="alert">
          {[geste.error, supprimer.error, suite.error]
            .filter(Boolean)
            .map((e) => (estApiError(e) ? e.message : t('commun:erreurReseau')))
            .join(' ')}
        </p>
      ) : null}
      {resultat === 'echec' ? (
        <p role="alert">{t('regie:offres.fiche.echec')}</p>
      ) : resultat === 'capture' ? (
        <p role="status">{t('regie:offres.fiche.capture')}</p>
      ) : resultat === 'envoye' ? (
        <p role="status">{t('regie:offres.fiche.envoye')}</p>
      ) : null}
      {dialogue === 'modifier' ? (
        <NouvelleOffreDialog
          open
          onOpenChange={(v) => {
            if (!v) setDialogue(null);
          }}
          offreInitiale={offre}
        />
      ) : null}
      {dialogue === 'contrePropose' ? (
        <NouvelleOffreDialog
          open
          onOpenChange={(v) => {
            if (!v) setDialogue(null);
          }}
          contactInitial={{ id: offre.contactId, nom: offre.contactNom }}
          contrePropositionDe={offre.numero}
          lignesInitiales={offre.lignes.map((l) => ({
            oeuvreId: l.oeuvreId,
            titre: l.cartel.titre,
            prixProposeCentimes: l.prixProposeCentimes,
            prixPublicCentimes: l.prixPublicCentimes,
            devise: offre.devise,
            editionSuivie: Boolean(l.exemplaireId),
            ...(l.exemplaireId ? { exemplaireId: l.exemplaireId } : {}),
          }))}
        />
      ) : null}
      {dialogue && dialogue !== 'modifier' && dialogue !== 'contrePropose' ? (
        <Dialog
          open
          title={t(`regie:offres.fiche.${dialogue}`)}
          closeLabel={t('commun:actions.fermer')}
          onOpenChange={(v) => {
            if (!v) setDialogue(null);
          }}
          description={t(
            dialogue === 'supprimer'
              ? 'regie:offres.fiche.confirmationSuppression'
              : dialogue === 'accepter'
                ? 'regie:offres.fiche.confirmationAcceptation'
                : courriel
                  ? 'regie:offres.fiche.messageCourriel'
                  : 'regie:offres.fiche.motif',
            { contact: offre.contactNom },
          )}
          footer={
            <Button
              loading={geste.isPending || supprimer.isPending}
              disabled={courriel && !(adresse ?? contact.data?.contact.emails[0])}
              onClick={() => {
                if (dialogue === 'supprimer')
                  void supprimer
                    .mutateAsync()
                    .then(() => navigate(`/${galerieSlug}/offres`))
                    .catch(() => {});
                else void executer(dialogue);
              }}
            >
              {t(`regie:offres.fiche.${dialogue}`)}
            </Button>
          }
        >
          {courriel ? (
            <div className={styles.formulaire}>
              <Field htmlFor="offre-adresse" label={t('regie:offres.fiche.destinataire')}>
                <Select
                  id="offre-adresse"
                  value={adresse ?? contact.data?.contact.emails[0] ?? ''}
                  onValueChange={setAdresse}
                  options={(contact.data?.contact.emails ?? []).map((value) => ({
                    value,
                    label: value,
                  }))}
                />
              </Field>
              <Field htmlFor="offre-message-envoi" label={t('regie:offres.nouvelle.message')}>
                <Textarea
                  id="offre-message-envoi"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Field>
            </div>
          ) : dialogue === 'refuser' || dialogue === 'retirer' ? (
            <Field htmlFor="offre-motif" label={t('regie:offres.fiche.motif')}>
              <Textarea id="offre-motif" value={motif} onChange={(e) => setMotif(e.target.value)} />
            </Field>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}
